import { useEffect, useState } from 'react'
import is from 'is_js'
import _ from '@strings'
import { useForm, FormProvider } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Sizer, Row, Column } from '@components/Ui/Grid'
import { setFields, userUpdateForm } from '@store/actions/app'
import { formatDate } from '@utils/helpers'
import { useDsgvoContext } from '@context'
import Field from '@components/Form/Field'
import Loading from '@components/Ui/Loading'
import Error from '@components/Ui/Error'
import { useGetForm } from '@hooks'
import config from '@config'
import styles from './CustomerForm.scss'
import { isDevelopment } from '@helpers'

const CustomerForm = ({ setValid }) => {
  const [hiddenFields, setHiddenFields] = useState({})
  const [fieldOverride, setFieldOverride] = useState({})
  const storedFields = useSelector((s) => s.fields)
  const [fields, error, loading] = useGetForm()
  const { setAccepted } = useDsgvoContext()
  const selectedStore = useSelector((s) => s.data.store)
  const selectedType = useSelector((s) => s.data.type)
  const selectedSlot = useSelector((s) => s.data.slot)
  const selectedChild = useSelector((s) => s.data.child)

  const defaultValues = isDevelopment()
    ? {
        appointment_title: 'Herr',
        appointment_firstname: 'Joe',
        appointment_lastname: 'Doe',
        appointment_email: 'john@doe.com',
        appointment_phone: '123456789',
        appointment_zip: '12345',
        appointment_city: 'Graz',
      }
    : {}

  const { register, unregister, clearErrors, formState, setValue, watch } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues,
  })
  const dispatch = useDispatch()

  const hasStoredFields = is.not.empty(storedFields || [])
  const hasFields = is.not.empty(fields || [])
  const showFields = hasStoredFields ? storedFields : fields

  // new customer conditional logic
  const isCustomer = watch('appointment_is_customer')

  useEffect(() => {
    if (config.get('local.features.conditional_customer_field') === true) {
      if (isCustomer?.[0] === '1') {
        setHiddenFields((s) => ({
          appointment_zip: true,
          appointment_city: true,
        }))

        setFieldOverride((s) => ({
          appointment_birthdate: { required: '1' },
        }))

        unregister('appointment_zip')
        unregister('appointment_city')
      } else {
        setHiddenFields((s) => ({
          appointment_zip: false,
          appointment_city: false,
        }))

        setFieldOverride((s) => ({
          appointment_birthdate: { required: '0' },
        }))
      }
    }
  }, [isCustomer])
  // over and out

  useEffect(() => {
    const data = watch()
    if (formState.isValid) {
      setValid(true)
      dispatch(userUpdateForm(data))
    } else {
      setValid(false)
      dispatch(userUpdateForm(null))

      setAccepted(data.appointment_terms?.[0] === '1')
    }
  }, [formState])

  useEffect(() => {
    if (!loading && hasFields) {
      dispatch(setFields(fields))
    }
  }, [fields, loading])

  useEffect(() => {
    let time = `${selectedSlot?.startTime.slice(0, -3)} - ${selectedSlot?.endTime.slice(0, -3)}`

    if (selectedSlot?.prefix) {
      time += ` (${selectedSlot?.prefix})`
    }

    if (selectedSlot?.startTime && !selectedSlot?.endTime && selectedSlot?.prefix) {
      time = selectedSlot?.prefix
    }

    // set hidden fields
    setValue('appointment_location', selectedStore?.contact?.formattedAddress)
    setValue('appointment_type', selectedType?.name)
    setValue('appointment_date', formatDate(Date.parse(selectedSlot?.date), 'dd.MMMM yyyy'))
    setValue('appointment_time', time)
    setValue('appointment_child', selectedChild ? 1 : 0)
    setValue('appointment_campaign', config.get('local.campaign') || '')

    // tracking
    setValue('usertrack_source', config.get('local.tracking.source'))
    setValue('usertrack_medium', config.get('local.tracking.medium'))
    setValue('usertrack_campaign', config.get('local.tracking.campaign'))
  }, [selectedStore, selectedType, selectedSlot])

  const getFieldColumnSize = (field) => {
    // This hack is needed since Formidable has no field size setting for dropdown fields.
    if (field?.field_key === 'appointment_title') {
      return '2/12@md'
    }
    if (field?.field_key === 'appointment_phone_prefix') {
      return '2/12@md'
    }

    return field?.size || '12/12'
  }

  return (
    <div>
      {error && <Error>{_('errors.api')}</Error>}
      {loading && !hasStoredFields && <Loading>Das Formular wird geladen</Loading>}
      <FormProvider
        register={register}
        clearErrors={clearErrors}
        setValue={setValue}
        watch={watch}
        errors={formState.errors}
      >
        <Row>
          {showFields?.map((field) => {
            if (hiddenFields[field?.field_key]) {
              return null
            }

            const override = fieldOverride[field?.field_key] || {}
            const fieldData = { ...field, ...override }

            return (
              <Column size={getFieldColumnSize(field)} key={field?.field_key}>
                <Field {...fieldData} />
              </Column>
            )
          })}
        </Row>
      </FormProvider>
    </div>
  )
}

CustomerForm.defaultProps = {}
CustomerForm.propTypes = {}

export default CustomerForm
